// The inspector: a header saying what is being edited (and where it lives),
// the PropertyPanel body, and an in-place host so the editors that used to be
// dialogs (lookup, display format, ID series, When it changes, validations…)
// open as a drill-in panel here instead of over the page. While one is open
// the panel widens over the canvas so wide editors keep their room.

import { useCallback, useMemo, useState } from 'react'
import { ChevronLeft, PanelRightClose, X } from 'lucide-react'
import type { TransactionModel, TreeNode } from './types'
import PropertyPanel from './PropertyPanel'
import { ModalInlineHostContext } from '@/components/common/Modal'
import { allSqlModels } from '@twasta/page-runtime/txn/utils'
import { formTitle, groupLabel } from './setupCatalog'

interface Props {
  model: TransactionModel
  selectedNode: TreeNode | null
  onSelect: (node: TreeNode) => void
  onModelChange: (m: TransactionModel) => void
  onOpenCode?: (method?: string) => void
  project: string
  onGuardedNavigate?: (action: () => void) => void
  onUndo?: () => void
  onOpenSettings?: (node: TreeNode) => void
  /** 'docked' (right panel), 'floating' (over the canvas), 'sheet' (phone),
   *  'page' (Settings workspace — no header, full width). */
  variant: 'docked' | 'floating' | 'sheet' | 'page'
  onHide?: () => void
  onDock?: () => void
}

const LIST_TITLES: Partial<Record<TreeNode['type'], string>> = {
  'form-actions': 'Form buttons',
  'object-actions': 'Record buttons',
  'form-field-events': 'Field events',
  'form-row-events': 'Row events',
  'form-validations': 'Validations',
  'form-joins': 'Joins',
  'form-cross-updates': 'Cross updates',
  'form-line-entry': 'Line button',
  'followup-actions': 'Follow-ups',
  'record-saving': 'Before / after saving',
  'pre-commit': 'Before saving (pre-commit)',
  'transaction-complete': 'After save (post-commit)',
  'cross-updates': 'Cross updates (legacy, record level)',
  approvals: 'Approvals',
  'record-settings': 'Record settings',
  'work-rules': 'Work rules',
  offline: 'Offline & sync',
  'assistant-authoring': 'Assistant authoring',
  'vector-search': 'Search by meaning',
  'file-import': 'File import',
  'additional-attributes': 'Additional attributes',
  attachments: 'Document attachments',
  website: 'Publish on website',
  object: 'General',
  columns: 'Columns',
}

export default function Inspector({
  model, selectedNode, onSelect, onModelChange, onOpenCode, project, onGuardedNavigate, onUndo,
  onOpenSettings, variant, onHide, onDock,
}: Props) {
  const [host, setHost] = useState<HTMLDivElement | null>(null)
  const [openCount, setOpenCount] = useState(0)
  const onOpenChange = useCallback((d: 1 | -1) => setOpenCount(n => Math.max(0, n + d)), [])
  const hostCtx = useMemo(() => ({ target: host, onOpenChange }), [host, onOpenChange])

  const head = useMemo(() => describe(model, selectedNode), [model, selectedNode])
  const closeLabel = variant === 'floating' || variant === 'sheet' ? 'Close' : 'Hide inspector  ]'

  return (
    <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, background: 'var(--color-bg-secondary)' }}>
      {variant === 'floating' && onDock && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px 8px 16px', background: 'var(--color-surface-2)',
          borderBottom: '1px solid var(--color-border)', borderRadius: '10px 10px 0 0', fontSize: '0.875rem',
          color: 'var(--color-text-secondary)', flexShrink: 0,
        }}>
          Floating — the inspector panel is hidden
          {onDock && (
            <button type="button" className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto' }} onClick={onDock}
              title="Put the inspector back in its panel  ]">Dock</button>
          )}
        </div>
      )}
      {variant !== 'page' && (
        <div style={{ padding: '12px 14px 10px', borderBottom: '1px solid var(--color-border)', flexShrink: 0 }}>
          {head.back ? (
            <button type="button" onClick={() => onSelect(head.back!)} style={{
              font: 'inherit', fontSize: '0.875rem', color: 'var(--color-accent)', border: 'none', background: 'none',
              padding: '2px 0', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 2,
            }}><ChevronLeft size={14} /> {head.crumb}</button>
          ) : (
            <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>{head.crumb}</div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
            <div style={{
              fontWeight: head.mono ? 500 : 600, fontSize: '1.0625rem', minWidth: 0, overflow: 'hidden',
              textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: head.mono ? 'var(--font-mono)' : 'inherit',
            }}>{head.title}</div>
            {head.badge && (
              <span style={{
                fontFamily: 'var(--font-mono)', fontSize: '0.8125rem', background: 'var(--color-surface-2)',
                border: '1px solid var(--color-border)', borderRadius: 4, padding: '1px 6px', whiteSpace: 'nowrap',
              }}>{head.badge}</span>
            )}
            {onHide && (
              <button type="button" onClick={onHide} aria-label={closeLabel} title={closeLabel} style={{
                marginLeft: 'auto', width: 30, height: 30, borderRadius: 6, display: 'inline-flex', alignItems: 'center',
                justifyContent: 'center', border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--color-text)', flexShrink: 0,
              }}>
                {variant === 'docked' ? <PanelRightClose size={16} /> : <X size={18} />}
              </button>
            )}
          </div>
        </div>
      )}
      <ModalInlineHostContext.Provider value={hostCtx}>
        <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <PropertyPanel
            model={model}
            selectedNode={selectedNode}
            onModelChange={onModelChange}
            onOpenCode={onOpenCode}
            project={project}
            onGuardedNavigate={onGuardedNavigate}
            onSelectNode={onSelect}
            onUndo={onUndo}
            onOpenSettings={onOpenSettings}
          />
        </div>
      </ModalInlineHostContext.Provider>
      {/* In-place editors draw here. Anchored to the right edge and at least
          640px wide while one is open, so it grows over the canvas. */}
      <div
        ref={setHost}
        style={{
          position: 'absolute', top: 0, bottom: 0, right: 0, zIndex: 20,
          width: openCount > 0 ? (variant === 'sheet' ? '100%' : 'max(100%, min(680px, 92vw))') : 0,
          pointerEvents: openCount > 0 ? 'auto' : 'none',
          boxShadow: openCount > 0 && variant !== 'sheet' ? '-12px 0 32px rgba(0,0,0,0.16)' : 'none',
          borderLeft: openCount > 0 ? '1px solid var(--color-border)' : 'none',
          background: openCount > 0 ? 'var(--color-bg-secondary)' : 'transparent',
          borderRadius: variant === 'floating' ? 10 : 0,
          overflow: 'hidden',
        }}
      />
    </div>
  )
}

function describe(model: TransactionModel, node: TreeNode | null): {
  crumb: string; title: string; badge?: string; mono?: boolean; back?: TreeNode
} {
  if (!node) return { crumb: 'Inspector', title: 'Nothing selected' }
  const entries = allSqlModels(model)
  const fi = node.formIndex
  const sm = fi != null ? entries[fi]?.sm : undefined
  const ftitle = fi != null ? formTitle(sm, fi) : ''
  const objTitle = model.obj_name || 'Record'
  if (node.type === 'column' && sm && node.columnIndex != null) {
    const c = sm.columns?.[node.columnIndex]?.column
    const type = c ? `${(c.col_type || '').toUpperCase()}${c.db_size && /CHAR|NUMBER|NUMERIC|DECIMAL/i.test(c.col_type || '') ? `(${c.db_size})` : ''}` : ''
    return {
      crumb: `${ftitle} › ${groupLabel(sm, c?.group_name || 'Others')} › Column`,
      title: c?.name || node.label, badge: type || undefined, mono: true,
    }
  }
  if (node.type === 'form') return { crumb: `Form ${entries[fi ?? 0]?.formNo || ''}`, title: ftitle }
  const listTitle = LIST_TITLES[node.type] || node.label
  const scope = fi != null ? ftitle : objTitle
  if ((node.type === 'form-actions' || node.type === 'object-actions') && node.actionIndex != null) {
    const list = node.type === 'form-actions' ? sm?.actions : model.actions
    const a = list?.[node.actionIndex] as { name?: string } | string | undefined
    const name = typeof a === 'string' ? a.split(':')[0] : (a?.name || node.label)
    return {
      crumb: `${scope} › ${listTitle}`, title: name || 'Unnamed button',
      back: { type: node.type, formIndex: fi, label: listTitle },
    }
  }
  if (node.type === 'form-line-entry') return { crumb: `${ftitle} › How lines are filled`, title: node.actionIndex != null ? 'Line button' : 'Line buttons' }
  return { crumb: scope, title: listTitle }
}
